import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { CAUSE_COLORS } from "@/lib/causes";
import { Sparkles, ArrowRight } from "lucide-react";

export default function AssessmentResult() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const cached = sessionStorage.getItem("cm_result");
    if (cached) {
      try {
        setData(JSON.parse(cached));
        return;
      } catch (e) { /* fallthrough */ }
    }
    // No cached result — fetch all causes to render fallback (matched are in user object)
    api.get("/auth/me").then(async ({ data: me }) => {
      const causesRes = await api.get("/causes");
      const map = Object.fromEntries(causesRes.data.map((c) => [c.id, c]));
      setData({
        primary: map[me.primary_cause],
        top: (me.matched_causes || []).map((id) => map[id]).filter(Boolean),
        scores: {},
      });
    });
  }, []);

  if (!data || !data.primary) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-20">
          <p className="text-ink-soft">Loading your match…</p>
        </div>
      </Layout>
    );
  }

  const { primary, top } = data;

  return (
    <Layout>
      <div data-testid="assessment-result-page" className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20 fade-up">
        <span className="overline text-[var(--cm-primary)] inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Your cause match
        </span>
        <h1 className="font-display font-bold text-4xl md:text-6xl mt-3 tracking-tight leading-[1.02]">
          You align most with
          <br />
          <span style={{ color: CAUSE_COLORS[primary.id] || primary.color }}>{primary.name}.</span>
        </h1>
        <p className="text-ink-soft mt-6 text-lg max-w-2xl leading-relaxed" data-testid="result-tagline">
          {primary.tagline} {primary.description}
        </p>

        {top.length > 1 && (
          <div className="mt-10">
            <span className="overline text-ink-soft">Also resonating</span>
            <div className="flex flex-wrap gap-2 mt-3">
              {top.slice(1).map((c) => (
                <span
                  key={c.id}
                  data-testid={`result-secondary-${c.id}`}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold text-white"
                  style={{ background: CAUSE_COLORS[c.id] || c.color }}
                >
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <Link
            to="/opportunities"
            data-testid="result-cta-opportunities"
            className="card-soft p-8 group hover:-translate-y-0.5 transition-transform"
          >
            <span className="overline text-[var(--cm-primary)]">Step 1</span>
            <h3 className="font-display font-semibold text-2xl mt-2">See volunteer opportunities</h3>
            <p className="text-ink-soft mt-2 text-sm">
              We've already filtered ones aligned with your match — but you can browse them all.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-[var(--cm-primary)] font-semibold">
              Browse opportunities <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
          <Link
            to="/dashboard"
            data-testid="result-cta-dashboard"
            className="card-soft p-8 group hover:-translate-y-0.5 transition-transform"
          >
            <span className="overline text-[var(--cm-secondary)]">Step 2</span>
            <h3 className="font-display font-semibold text-2xl mt-2">Set up your dashboard</h3>
            <p className="text-ink-soft mt-2 text-sm">
              Track your hours, upcoming shifts, and balance in one place.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-[var(--cm-secondary)] font-semibold">
              Go to dashboard <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
